'use client';

import { FC, Fragment, ReactNode, useEffect, useState } from 'react';

import classNames from 'classnames';

import dataAttributes from '../../../../utils/attributes/attributes';
import { Tab, TabProps } from '../Tab/Tab';

interface Props {
   tabs: Array<TabProps>;
   activeTab?: number;
   showContainer?: boolean;
   stretch?: boolean;
   height?: string;
   visualizeChanges?: boolean;
   alignment?: 'left' | 'center' | 'right';
   uniform?: boolean;
   tabStyle?: 'simple' | 'classic';
   onTabChange?: (index: number) => void;
   onTabClick?: (index: number) => void;
   attributes?: Record<string, string>;
   renderItem?: (index: number, children: ReactNode) => ReactNode;
}

export const Tabs: FC<Props> = ({ tabs, activeTab = 0, showContainer = true, stretch = false, height, visualizeChanges = true, alignment = 'left', onTabChange, onTabClick, uniform = false, tabStyle = 'simple', attributes, renderItem }) => {
   const [currentTab, setCurrentTab] = useState(activeTab);

   const handleTabClick = (index: number) => {
      if (index !== currentTab && visualizeChanges) {
         setCurrentTab(index);
         onTabChange?.(index);
      }

      onTabClick?.(index);
   };

   useEffect(() => setCurrentTab(activeTab), [activeTab]);

   const renderTab = (index: number, tab: ReactNode) => (renderItem ? renderItem(index, tab) : tab);

   return (
      <div
         {...dataAttributes(attributes)}
         data-testid="tabs-container"
         className={classNames(
            'w-full flex flex-col',
            { 'items-start': alignment === 'left' },
            { 'items-center': alignment === 'center' },
            { 'items-end': alignment === 'right' },
            { 'box-shadow-component-navigation-tabs-container': showContainer },
            { 'box-shadow-component-navigation-tabs-container-color-simple': showContainer && tabStyle === 'simple' },
            { 'box-shadow-component-navigation-tabs-container-color-classic': showContainer && tabStyle === 'classic' }
         )}
         style={{
            boxShadow: tabStyle === 'simple' ? 'inset 0 calc(-1 * var(--component-navigation-tab-container-border-width-inactive)) 0 var(--component-navigation-tab-container-color-border-inactive)' : undefined
         }}
      >
         <div data-testid={`tabs${stretch ? '--stretched' : ''}`} className={classNames('grid', { 'w-full': stretch }, { 'gap-component-navigation-tabs-spacing-gap': tabStyle === 'classic' })} style={{ height, gridTemplateColumns: `repeat(${tabs.length}, minmax(0, ${uniform ? '1fr' : 'auto'}))` }}>
            {tabs.map((tab, index) => (
               // eslint-disable-next-line @eslint-react/no-array-index-key
               <Fragment key={index}>
                  {renderTab(index, <Tab attributes={tab.attributes} tabStyle={tabStyle} width={stretch ? '100%' : undefined} id={tab.id} label={tab.label} badge={tab.badge} leadingIcon={tab.leadingIcon} trailingIcon={tab.trailingIcon} active={currentTab === index} onClick={() => handleTabClick(index)} />)}
               </Fragment>
            ))}
         </div>
      </div>
   );
};
